<template>
    <div
        id="lt-yantr"
        class="advanced-calc home-refinance-calc">
        <div class="container-width">
            <div class="advanced-calc-filter">
                <label class="filter-header"><span class="hidden-xs">Your</span> Current Mortgage</label>
                <ul class="filter-inputs four-col">
                    <li>
                        <number-input
                            v-model="OriginalAmount"
                            :label="labelOriginalAmount"
                            inputmode="decimal"
                        />
                    </li>
                    <li>
                        <div class="form-group">
                            <label>{{ loanStartDate }}</label>
                            <datepicker
                                v-model="CurrentDate"
                                format="MM/DD/YYYY"
                                lang="en">
                                <span slot="icon-calendar"><img
                                    alt="calendar icon"
                                    src="../../assets/images/date-picker.png"
                                /></span>
                            </datepicker>
                        </div>
                    </li>
                    <li>
                        <drop-down
                            v-model="LoanTermInYears"
                            :label="labelLoanTerm"
                            :options="LoanTermInYearsList"
                            :tooltip-link="loanTermTooltip"
                        />
                    </li>
                    <li>
                        <number-input
                            v-model="InterestRate"
                            :label="labelInterestRate"
                            symbol="%"
                            symbol-aligned="right"
                            precision="3"
                            max-value="100.000"
                            :tooltip-link="interestRateTooltip"
                            inputmode="decimal"
                        />
                    </li>
                </ul>
                <label class="filter-header"><span class="hidden-xs">Your</span> New Mortgage</label>
                <ul class="filter-inputs four-col mob-ai-baseline">
                    <li>
                        <drop-down
                            v-model="NewLoanTermInYears"
                            :label="labelNewLoanTerm"
                            :options="LoanTermInYearsList"
                        />
                    </li>
                    <li>
                        <number-input
                            v-model="NewInterestRate"
                            :label="labelNewInterestRate"
                            symbol="%"
                            symbol-aligned="right"
                            precision="3"
                            max-value="100.000"
                            inputmode="decimal"
                        />
                    </li>
                    <li>
                        <number-input
                            v-model="LenderFees"
                            :label="labelLenderFees"
                            symbol="%"
                            symbol-aligned="right"
                            precision="3"
                            max-value="100.000"
                            inputmode="decimal"
                        />
                    </li>
                    <li class="white-space-nowrap">
                        <number-input
                            v-model="PlanStayYears"
                            :label="labelLengthOwnership"
                            symbol="Years"
                            symbol-aligned="right"
                            inputmode="decimal"
                        />
                    </li>
                </ul>
                <a
                    class="reset-calc hidden-xs"
                    @click="resetFilter()"><span class="lt-icons lt4-Refresh"></span> Reset Calculator</a>
            </div>

            <refinancing-makes-sense
                :disclosure-text="DisclosureText"
                :disclosure-tokens="DisclosureTokens"
                :lt-logo="LtLogo"
                :destination-link="DestinationLinkOrId"
                :destination-button-text="DestinationButtonText"
                :original-amount="unformatFigure(OriginalAmount)"
                :current-loan-term-in-years="LoanTermInYears"
                :ref-loan-term-in-years="NewLoanTermInYears"
                :current-interest-rate="InterestRate"
                :ref-interest-rate="NewInterestRate"
                :plan-stay-in-years="PlanStayYears"
                :lender-fees="LenderFees"
                :start-date="CurrentDate"
                :property-value="unformatFigure(PropertyValue)"
            />
        </div>
    </div>
</template>

<script>
    import Accounting from 'accounting';
    import Datepicker from 'vue2-datepicker';
    import NumberInput from '~/components/number-input.vue';
    import DropDown from '~/components/drop-down.vue';
    import LoanTermList from '~/constants/loan-term-list';
    import DefaultTooltip from '~/constants/tooltip-defaults';
    import BaseMixin from '~/mixin/base-mixin';
    import RefinancingMakesSense from './components/refinancing-makes-sense.vue';

    export default {
        components: {
            NumberInput,
            DropDown,
            Datepicker,
            RefinancingMakesSense,
        },
        mixins: [BaseMixin],
        props: {
            labelPropertyValue: {
                default: 'Property Value:',
            },
            propertyValue: {
                default: '350000',
            },
            labelOriginalAmount: {
                default: 'Original Amount:',
            },
            originalAmount: {
                default: '308000',
            },
            loanStartDate: {
                default: 'Loan Start Date:',
            },
            labelLoanTerm: {
                default: 'Loan Term:',
            },
            loanTermInYears: {
                default: '30',
            },
            labelNewLoanTerm: {
                default: 'Loan Term:',
            },
            newLoanTermInYears: {
                default: '15',
            },
            loanTermTooltip: {
                default: DefaultTooltip.LoanTerm,
            },
            labelInterestRate: {
                default: 'Interest Rate:',
            },
            interestRate: {
                default: '5.000',
            },
            labelNewInterestRate: {
                default: 'Interest Rate:',
            },
            newInterestRate: {
                default: '4.300',
            },
            labelLengthOwnership: {
                default: 'Length of Ownership:',
            },
            planStayYears: {
                type: Number,
                default: 5,
            },
            interestRateTooltip: {
                default: DefaultTooltip.InterestRate,
            },
            labelLenderFees: {
                default: 'Lender & Title Fees:',
            },
            lenderFees: {
                default: '2.000',
            },
            customizeButtonText: {
                default: 'Customize',
            },
            showAdvancedOptions: {
                default: false,
            },
            recalculateButtonText: {
                default: 'Recalculate',
            },
            ltLogo: {
                default: 0,
            },
            disclosureTokens: {
                default: '95',
            },
            disclosureText: {
                default: 'Disclosures',
            },
            destinationLinkOrId: {
                default: 'wp-refinance',
            },
            destinationButtonText: {
                default: 'See Refinance Offers',
            },
            calcName: {
                default: '',
            },
            calcParent: {
                default: '',
            },
        },
        data() {
            return {
                ShowAdvancedOptions: this.showAdvancedOptions,
                LoanTermInYearsList: LoanTermList.LoanTermInYearsList.refinance,
                InterestRateTooltip: parseInt(this.hideTooltip, 10) ? '' : this.interestRateTooltip,
                LoanTermInYears: this.loanTermInYears,
                NewLoanTermInYears: this.newLoanTermInYears,

                DisclosureText: this.disclosureText,
                DisclosureTokens: this.disclosureTokens,
                LtLogo: this.ltLogo,
                DestinationLinkOrId: this.destinationLinkOrId,
                DestinationButtonText: this.destinationButtonText,
                CalcName: this.calcName,
                CalcParent: this.calcParent,
                // Internal data properties
                internalCurrentDate: new Date(2012, 4, 1, 0, 0, 0, 0),
                internalPropertyValue: this.propertyValue,
                internalOriginalAmount: this.originalAmount,
                internalInterestRate: this.interestRate,
                internalNewInterestRate: this.newInterestRate,
                internalLenderFees: this.lenderFees,
                internalPlanStayYears: this.planStayYears,
            };
        },
        computed: {
            // Computed properties with getter/setter instead of self-cycled watchers
            CurrentDate: {
                get() {
                    return this.internalCurrentDate;
                },
                set(value) {
                    const now = new Date();
                    if (!value || value > now) {
                        this.internalCurrentDate = now;
                    } else {
                        this.internalCurrentDate = value;
                    }
                },
            },
            PropertyValue: {
                get() {
                    return this.internalPropertyValue;
                },
                set(value) {
                    const unformattedValue = this.unformatFigure(value);
                    this.internalPropertyValue = unformattedValue < 0 ? this.formatFigure(0) : value;
                },
            },
            OriginalAmount: {
                get() {
                    return this.internalOriginalAmount;
                },
                set(value) {
                    const unformattedValue = this.unformatFigure(value);
                    this.internalOriginalAmount = unformattedValue < 0 ? this.formatFigure(0) : value;
                },
            },
            InterestRate: {
                get() {
                    return this.internalInterestRate;
                },
                set(value) {
                    const numericValue = parseFloat(value) || 0;
                    this.internalInterestRate = Math.min(Math.max(numericValue, 0), 100);
                },
            },
            NewInterestRate: {
                get() {
                    return this.internalNewInterestRate;
                },
                set(value) {
                    const numericValue = parseFloat(value) || 0;
                    this.internalNewInterestRate = Math.min(Math.max(numericValue, 0), 100);
                },
            },
            PlanStayYears: {
                get() {
                    return this.internalPlanStayYears;
                },
                set(value) {
                    this.internalPlanStayYears = this.validatePlanStayYears(value);
                },
            },
            LenderFees: {
                get() {
                    return this.internalLenderFees;
                },
                set(value) {
                    const numericValue = parseFloat(value) || 0;
                    this.internalLenderFees = Math.min(Math.max(numericValue, 0), 100);
                },
            },
        },
        watch: {
            LoanTermInYears() {
                // Re-validate PlanStayYears when loan term changes
                this.internalPlanStayYears = this.validatePlanStayYears(this.internalPlanStayYears);
            },
        },
        methods: {
            validatePlanStayYears(value) {
                const numericValue = parseFloat(value) || 0;
                const minValue = numericValue <= 0 ? 1 : numericValue;
                return Math.min(minValue, this.LoanTermInYears);
            },
            viewMoreClick() {
                this.ShowAdvancedOptions = !this.ShowAdvancedOptions;
            },
            resetFilter() {
                this.PropertyValue = this.formatFigure(this.propertyValue);
                this.OriginalAmount = this.formatFigure(this.originalAmount);
                this.InterestRate = Accounting.formatNumber(this.interestRate, { precision: 3 });
                this.NewInterestRate = Accounting.formatNumber(this.newInterestRate, { precision: 3 });
                this.LenderFees = Accounting.formatNumber(this.lenderFees, { precision: 3 });
                this.PlanStayYears = this.planStayYears;
                this.CurrentDate = new Date(2012, 4, 1, 0, 0, 0, 0);
                this.LoanTermInYears = this.loanTermInYears;
                this.NewLoanTermInYears = this.newLoanTermInYears;
            },
        },
    };
</script>
<style src="../../assets/styles/common.scss" lang="scss"></style>
<style src="../../assets/styles/calc-advanced-filter.scss" lang="scss"></style>
<style src="./main.scss" lang="scss"></style>
